/**
 * Per-sector "Inside the demo" gallery data.
 *
 * Each sector has a list of groups, where each group has a title, a short
 * description, and an array of screenshots.
 *
 * Image files live in /public/images/<sector-folder>/<slug>.jpg
 */

export const sectorGalleries = {
  'construction': {
    intro:
      'A walk-through of the Videra Construction+ workspace — Tier-1 main-contractor delivery on the CPlus 7 / RIBA Plan of Work framework, with portfolio-grade reporting, lifecycle planning and Building Safety Act 2022-ready governance baked in.',
    folder: 'construction',
    groups: [
      {
        title: 'Portfolio Command Centre',
        desc:
          'Single pane of glass for the Tier-1 main contractor — board-grade KPIs, top risks, ITP pass-rate and forward gate calendar in one view.',
        items: [
          {
            file: 'cp-portfolio-pmo.jpg',
            caption: 'PMO — Portfolio Command Centre',
            note: 'KPIs, heat-map, forward gate calendar, RACI matrix and top-of-risk projects. Region-aware to UK (RIBA / Building Safety Act 2022) governance.',
          },
          {
            file: 'cp-executive-report.jpg',
            caption: 'Executive Report — Portfolio posture',
            note: '54 projects, £3.03bn contract value, £1.0m variation exposure, 70% ITP pass rate, top-10 severity-weighted risks with owner and due date.',
          },
        ],
      },
      {
        title: 'Live portfolio & projects',
        desc:
          'Every Tier-1 project on the CPlus 7 / RIBA framework — stage, framework, value, spend % and RAG at a glance.',
        items: [
          {
            file: 'cp-live-portfolio.jpg',
            caption: 'Live portfolio',
            note: '52 projects across the CPlus 7 framework — Kingsmere Heights, Pennine Diagnostic Centre, Iron & Steel House, Aire Valley Logistics Hub and more.',
          },
          {
            file: 'cp-project-stages.jpg',
            caption: 'Project — RIBA Plan of Work',
            note: 'AMC-1021 Pennine Diagnostic Centre · £54.7m · RIBA Stage 4 TEC · Technical Design with gate-approval pending and Golden-Thread sign-off ready.',
          },
        ],
      },
      {
        title: 'Lifecycle, assets & handover',
        desc:
          'BIM-to-AIM bridge via IFC GUID. Asset register, 5-year replacement plan and frequency-driven PPM roll-over from day one.',
        items: [
          {
            file: 'cp-lifecycle.jpg',
            caption: 'Lifecycle — 5-Yr Plan Builder',
            note: '1,825 assets logged, 995 BIM-linked, £430.3m replacement value. Annual CAPEX profile FY26–FY31 generated from condition, criticality and expected life.',
          },
          {
            file: 'cp-asset-register.jpg',
            caption: 'Asset register & CAPEX programme',
            note: '53 replacement packages on a six-year timeline, criticality-coloured (Mission-Critical → Minor), CAPEX & in-delivery status per package.',
          },
        ],
      },
      {
        title: 'Safety, compliance & templates',
        desc:
          'Portfolio-wide RAMS, compliance-calendar deadlines and a versioned catalogue of stage-task templates — Building Safety Act 2022-ready by default.',
        items: [
          {
            file: 'cp-safety-rams.jpg',
            caption: 'Safety, RAMS & audits',
            note: '13 RAMS · 9 approved · 3 draft · 2 expiring · 2 expired. SafetyAI weekly brief feeds the board pack automatically.',
          },
          {
            file: 'cp-compliance-calendar.jpg',
            caption: 'Compliance Calendar',
            note: 'Tenant-wide insurance, ISO, CIS plus project-pinned Gateway 2, F10 and soft-landings — completion writes a row into the Golden Thread.',
          },
          {
            file: 'cp-stage-templates.jpg',
            caption: 'Stage-task templates',
            note: 'Versioned (draft → published) catalogues — CPlus-7 Standard Delivery, 36 tasks across stages 0–6, applied without overwriting existing work.',
          },
        ],
      },
      {
        title: 'Commercial — bids & supply chain',
        desc:
          'Outside the delivery portfolio: pre-construction pipeline, BidAI narrative and a live PQQ-tracked supplier register.',
        items: [
          {
            file: 'cp-bid-pipeline.jpg',
            caption: 'Bid pipeline & BidAI',
            note: '8 bids · £728m active pipeline · 50% win rate · 59% avg win prob. BidAI drafts a four-paragraph narrative anchored in your most relevant won projects.',
          },
          {
            file: 'cp-suppliers.jpg',
            caption: 'Supplier register & PQQ',
            note: '15 suppliers · 10 approved · 2 under review · 3 insurance ≤90d · 3 expired. PQQ packs, insurance expiries and live-variation bindings on one page.',
          },
        ],
      },
      {
        title: 'Onboarding & multi-tenant',
        desc:
          'Spin up a new region in minutes — multi-tenant by design, UK / CA frameworks toggled by a region pill.',
        items: [
          {
            file: 'cp-welcome-splash.jpg',
            caption: 'Portfolio Hub — site switcher',
            note: 'Tier-1 Main Contractor pane with 105 projects · £6.02bn programme. UK and CA frameworks live, switchable per tenant.',
          },
        ],
      },
    ],
  },

  'nhs': {
    intro:
      'A walk-through of the NHS Estates workspace — from Trust-wide capital programme down to a single RIBA-staged project, all aligned to RIBA Plan of Work, KSAR Gates and HAI-SCRIBE in one synchronised view.',
    folder: 'nhs',
    groups: [
      {
        title: 'Capital Programme Command Centre',
        desc:
          'Trust-wide visibility of every scheme — RAG mix, slippage, sector spend rollup and headline KPIs in a single, board-credible view.',
        items: [
          {
            file: 'nhs-portfolio-tracker.jpg',
            caption: 'NHS Portfolio Tracker',
            note: '57 schemes, £721.3M budget, RAG mix (47G · 0A · 10R), avg slippage and in-construction count, with sector spend rollup beneath.',
          },
        ],
      },
      {
        title: 'Trust Board pack & PMO',
        desc:
          'Board-ready capital programme reporting — generated from the same data PMs already capture. No more cut-and-paste board packs.',
        items: [
          {
            file: 'nhs-board-pack.jpg',
            caption: 'NHS Board Pack — Quarterly',
            note: 'Q2-2026 Capital Programme Position Statement: 69 schemes in flight, £1,137.2M portfolio value, programme momentum and board narrative — one click to PDF.',
          },
          {
            file: 'nhs-pmo-command-centre.jpg',
            caption: 'PMO Command Centre',
            note: 'Single pane of glass across the Trust — RIBA stage distribution, HBN/HTM compliance signal, RAG, PM capacity and lessons learned.',
          },
          {
            file: 'nhs-governance-reports.jpg',
            caption: 'Governance Reports',
            note: 'Live RAG and narrative per project — schedule, cost, scope and safety scoring, with board-action flags and an audit-trail by default.',
          },
        ],
      },
      {
        title: 'Projects, RIBA & assurance',
        desc:
          'Every scheme delivered through the RIBA Plan of Work 2020 — with assurance packs and AI analysis on tap.',
        items: [
          {
            file: 'nhs-projects-list.jpg',
            caption: 'NHS Projects',
            note: 'Every project across all funding pots — Capital, Rev Sec and beyond — filtered by funding pot, RIBA stage and RAG.',
          },
          {
            file: 'nhs-project-riba-stages.jpg',
            caption: 'Project — RIBA Plan of Work',
            note: 'RIBA S0 → S7 with stage progress, gate-doc sign-off, budget and AI analyse / Assurance Pack download in one click.',
          },
        ],
      },
      {
        title: 'Three-rail compliance — RIBA · KSAR · HAI-SCRIBE',
        desc:
          'The piece nobody else does. Capital, business case and infection-control compliance synchronised on one rail per project.',
        items: [
          {
            file: 'nhs-compliance-rails.jpg',
            caption: 'Synchronised compliance journey',
            note: 'RIBA Plan of Work · KSAR Gates · HAI-SCRIBE Phases lined up, with required deliverables auto-checked against each stage.',
          },
          {
            file: 'nhs-ipc-dashboard.jpg',
            caption: 'IPC Dashboard (HAI-SCRIBE)',
            note: 'Cross-project infection-prevention view — open risks, per-project completion vs RIBA stage and stages signed-off.',
          },
        ],
      },
      {
        title: 'Finance integrations',
        desc:
          'Plug straight into the Trust\'s ledger — automatic reconciliation and board-ready CVR reports.',
        items: [
          {
            file: 'nhs-financial-systems.jpg',
            caption: 'NHS Finance Integrations',
            note: 'Native connectors to SBS Finance, Oracle EBS / Fusion Cloud and Unit4 ERP — with Access, Sage and custom stacks under consultation.',
          },
        ],
      },
    ],
  },

  'hard-services': {
    intro:
      'A walk-through of the FM Hard Services workspace — from the Sector Command Centre down to RFIs, submittals and CDM evidence on a single project. Every screen is a real view from the live demo.',
    folder: 'healthcare',
    groups: [
      {
        title: 'Sector Command Centre',
        desc:
          'Cross-site rollup for the entire Hard Services sector. Drill into any site, surface resource flags, or generate a board-ready PDF in a click.',
        items: [
          {
            file: 'hc-sector-dashboard.jpg',
            caption: 'Hard Services — Sector Command Centre',
            note: 'Sites, projects, programme £, forecast, slippage, RAG mix and PM resource flags on one page.',
          },
        ],
      },
      {
        title: 'Portfolio overview',
        desc:
          'Operational dashboards used by Sector Directors and Heads of FM to keep every site visible at a glance.',
        items: [
          {
            file: 'hc-dashboard.jpg',
            caption: 'Workspace dashboard',
            note: 'Daily landing page summarising live projects, open items and next gate reviews.',
          },
          {
            file: 'hc-portfolio-tracker.jpg',
            caption: 'Portfolio Tracker',
            note: 'Every project on a single line — stage, status, RAG, budget, slip and handback readiness.',
          },
          {
            file: 'hc-portfolio-tracker-snapshot.jpg',
            caption: 'Project snapshot drawer',
            note: 'Inline drawer: budget burn, forecast variance, slippage, gate dwell and open governance.',
          },
        ],
      },
      {
        title: 'Projects, stages & gates',
        desc:
          'Stage-gated delivery from Start-Up through Handover. Every gate has clear entry, exit and assurance criteria.',
        items: [
          {
            file: 'hc-projects-list.jpg',
            caption: 'Projects list',
            note: 'All Hard Services projects with sector, lifecycle stage and assigned PM.',
          },
          {
            file: 'hc-project-stages.jpg',
            caption: 'Project lifecycle',
            note: 'SU → PL → HC → CO → HO with progress, deliverables and gate-by-gate status.',
          },
          {
            file: 'hc-projects-gate-overview.jpg',
            caption: 'Gate stage overview',
            note: 'Full visibility of where each project sits and what is required to advance.',
          },
          {
            file: 'hc-projects-gates-closed.jpg',
            caption: 'CO — Construction gate',
            note: 'Gate Progress, Stage Checklist and deliverables — auto-validated against documentation.',
          },
          {
            file: 'hc-projects-commercial.jpg',
            caption: 'Commercial view',
            note: 'Budget, committed, forecast and variation tracking inside the project record.',
          },
        ],
      },
      {
        title: 'Submittals, RFIs & H&S',
        desc:
          'PFI/FM-shaped workflows: Specialist creates → Contractor reviews → AP/AE → SPV/Trust. Evidence, not email.',
        items: [
          {
            file: 'hc-submittals.jpg',
            caption: 'Submittals register',
            note: 'PFI workflow — Total, Contractor Review, AP/AE Review, SPV/Trust, Approved and Overdue at a glance.',
          },
          {
            file: 'hc-submittals-dialog.jpg',
            caption: 'New Submittal dialog',
            note: 'Structured template capture — no PDFs, no rework, audit-ready by default.',
          },
          {
            file: 'hc-rfis.jpg',
            caption: 'RFIs register',
            note: 'Open, responded and overdue RFIs with full thread, links to projects and SLA timers.',
          },
          {
            file: 'hc-projects-hs-gate.jpg',
            caption: 'CDM Documentation',
            note: 'Required CDM docs auto-checked against the stage gate — H&S Team sign-off is a workflow, not an email.',
          },
          {
            file: 'hc-health-safety-approvals.jpg',
            caption: 'Health & Safety approvals',
            note: 'Live H&S approval queue with status, owner and last review timestamp.',
          },
        ],
      },
      {
        title: 'Governance & PMO',
        desc:
          'Weekly governance, board packs and exception reporting — generated from the same data your PMs already capture.',
        items: [
          {
            file: 'hc-weekly-governance.jpg',
            caption: 'Weekly governance — overview',
            note: 'Standing agenda, actions, decisions and risks per site, ready for the meeting.',
          },
          {
            file: 'hc-weekly-governance-2.jpg',
            caption: 'Weekly governance — detail',
            note: 'Detailed governance pages with linked evidence per project.',
          },
          {
            file: 'hc-governance-weekly-dialog.jpg',
            caption: 'Weekly update dialog',
            note: 'Single structured update captures status, blockers and asks — feeds the board pack automatically.',
          },
          {
            file: 'hc-pmo.jpg',
            caption: 'PMO Command Centre',
            note: 'Cross-portfolio PMO view — owners, due dates, exceptions and assurance status.',
          },
        ],
      },
      {
        title: 'Asset register & handback',
        desc:
          'Concession-end ready. Every asset linked to the project that delivered it.',
        items: [
          {
            file: 'hc-asset-register.jpg',
            caption: 'Asset register',
            note: 'Full asset list with condition, residual life and links to projects, RAMS and test certificates.',
          },
          {
            file: 'hc-handback-readiness.jpg',
            caption: 'Handback readiness',
            note: 'Concession-end readiness scoring per site — gaps surfaced months, not weeks, before expiry.',
          },
        ],
      },
      {
        title: 'Workspace setup',
        desc:
          'Spin up a new workspace, brand it and link it into a multi-site portfolio in minutes.',
        items: [
          {
            file: 'hc-workspace-studio.jpg',
            caption: 'Workspace Studio',
            note: 'Identity, branding, users and workspace data — managed in one place.',
          },
        ],
      },
    ],
  },
};
